import { useState } from "react";
import MultiSelectField from "../../../shared/forms/MultiSelectField";
import RangeSliderField from "../../../shared/forms/RangeSliderField";
import MultiCountryStateCitySelector from "../../../shared/common/MultiCountryStateCitySelector";
import FullWidthHobbiesGrid from "../../../shared/components/FullWidthHobbiesGrid";
import { Country } from "country-state-city";

import {
  brideGroomCategoryOptions,
  formatHeightValue,
  preferredAgeRangeConfig,
  preferredHeightRangeConfig,
  preferredIncomeRangeConfig,
  preferredMaritalStatusOptions,
  subCasteOptions,
} from "../../../shared/config/profileOptions";

const inputClass =
  "mt-1 block w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const sectionClass =
  "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6";

const sectionTitleClass =
  "text-base font-semibold text-slate-800";

const sectionHelpClass =
  "mt-1 text-sm text-slate-500";

const PartnerPreferencesSection = ({
  formData,
  updateField,
  setFieldValue,
  gotraOptions,
  rashiOptions,
  nakshatraOptions,
  searchMotherTongues,
  searchEducation,
  searchProfessions,
  searchGuruMatha,
  searchPlaces,
}) => {
  const [nativeInput, setNativeInput] = useState("");
  const [nativeOptions, setNativeOptions] = useState([]);
  const [nativeLoading, setNativeLoading] = useState(false);

  const expectationsCount = String(
    formData.expectations || ""
  ).length;

  const lookup = async (searchFn, text) => {
    if (!text || text.trim().length < 2) {
      return [];
    }

    const result = await searchFn(text);

    return Array.isArray(result)
      ? result
      : result?.data || result?.results || [];
  };

  const preferredCountryOptions =
    Country.getAllCountries().map((country) => ({
      label: country.name,
      value: country.isoCode,
    }));

  const searchNativePlaces = async (text) => {
    setNativeInput(text);

    if (!text || text.trim().length < 2) {
      setNativeOptions([]);
      return;
    }

    setNativeLoading(true);

    try {
      const result = await searchPlaces(text);
      setNativeOptions(
        Array.isArray(result) ? result : []
      );
    } finally {
      setNativeLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className={sectionClass}>
        <div className="mb-5">
          <h3 className={sectionTitleClass}>
            Basic Partner Preferences
          </h3>
          <p className={sectionHelpClass}>
            Define the basic profile range you would prefer.
          </p>
        </div>

        <div className="grid gap-6">
          <RangeSliderField
            label="Preferred Age Range"
            value={formData.ageRange}
            min={preferredAgeRangeConfig.min}
            max={preferredAgeRangeConfig.max}
            step={preferredAgeRangeConfig.step}
            onChange={(value) =>
              setFieldValue("ageRange", value)
            }
            formatValue={(value) => `${value} yrs`}
          />

          <RangeSliderField
            label="Preferred Height Range"
            value={formData.heightRange}
            min={preferredHeightRangeConfig.min}
            max={preferredHeightRangeConfig.max}
            step={preferredHeightRangeConfig.step}
            onChange={(value) =>
              setFieldValue("heightRange", value)
            }
            formatValue={formatHeightValue}
          />

          <RangeSliderField
            label="Preferred Annual Income Range (₹ Lakh)"
            value={formData.preferredIncomeRange}
            min={preferredIncomeRangeConfig.min}
            max={preferredIncomeRangeConfig.max}
            step={preferredIncomeRangeConfig.step}
            onChange={(value) =>
              setFieldValue("preferredIncomeRange", value)
            }
            formatValue={(value) => `₹${value}L`}
          />
        </div>

        <div className="mt-6 grid gap-5 md:grid-cols-2">
          <MultiSelectField
            label="Preferred Mother Tongue"
            value={formData.preferredMotherTongues || []}
            searchFn={(text) =>
              lookup(searchMotherTongues, text)
            }
            onChange={(value) =>
              setFieldValue(
                "preferredMotherTongues",
                value
              )
            }
          />

          <MultiSelectField
            label="Preferred Education"
            value={formData.preferredEducation || []}
            searchFn={(text) =>
              lookup(searchEducation, text)
            }
            onChange={(value) =>
              setFieldValue("preferredEducation", value)
            }
          />

          <label className="block text-sm font-medium text-slate-700">
            Preferred Marital Status
            <select
              name="preferredMaritalStatus"
              value={formData.preferredMaritalStatus || ""}
              onChange={updateField}
              className={inputClass}
            >
              <option value="">Select</option>
              {preferredMaritalStatusOptions.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Preferred Bride/Groom Category
            <select
              name="preferredBrideGroomCategory"
              value={
                formData.preferredBrideGroomCategory || ""
              }
              onChange={updateField}
              className={inputClass}
            >
              <option value="">Select</option>
              {brideGroomCategoryOptions.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className={sectionClass}>
        <div className="mb-5">
          <h3 className={sectionTitleClass}>
            Cultural & Family Preferences
          </h3>
          <p className={sectionHelpClass}>
            Capture cultural, community and spiritual preferences.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          <MultiSelectField
            label="Preferred Sub Caste"
            options={subCasteOptions}
            value={formData.preferredSubCastes || []}
            onChange={(value) =>
              setFieldValue("preferredSubCastes", value)
            }
          />

          <MultiSelectField
            label="Preferred Gotra"
            options={gotraOptions || []}
            value={formData.preferredGotras || []}
            onChange={(value) =>
              setFieldValue("preferredGotras", value)
            }
          />

          <MultiSelectField
            label="Preferred Rashi"
            options={rashiOptions || []}
            value={formData.preferredRashis || []}
            onChange={(value) =>
              setFieldValue("preferredRashis", value)
            }
          />

          <MultiSelectField
            label="Preferred Nakshatra"
            options={nakshatraOptions || []}
            value={formData.preferredNakshatras || []}
            onChange={(value) =>
              setFieldValue("preferredNakshatras", value)
            }
          />

          <MultiSelectField
            label="Preferred Guru Matha"
            value={formData.preferredGuruMathas || []}
            searchFn={(text) =>
              lookup(searchGuruMatha, text)
            }
            onChange={(value) =>
              setFieldValue("preferredGuruMathas", value)
            }
          />
        </div>
      </section>

      <section className={sectionClass}>
        <div className="mb-5">
          <h3 className={sectionTitleClass}>
            Career Preference
          </h3>
          <p className={sectionHelpClass}>
            Add the professional background you would prefer.
          </p>
        </div>

        <div className="max-w-3xl">
          <MultiSelectField
            label="Preferred Profession"
            value={formData.preferredProfessions || []}
            searchFn={(text) =>
              lookup(searchProfessions, text)
            }
            onChange={(value) =>
              setFieldValue("preferredProfessions", value)
            }
          />
        </div>
      </section>

      <section className={sectionClass}>
        <div className="mb-5">
          <h3 className={sectionTitleClass}>
            Preferred Hobbies & Activities
          </h3>
          <p className={sectionHelpClass}>
            Select general hobbies and spiritual or community activities.
          </p>
        </div>

        <FullWidthHobbiesGrid
          label="Preferred Hobbies"
          fieldName="preferredHobbies"
          formData={formData}
          handleChange={(event) =>
            setFieldValue(
              "preferredHobbies",
              event.target.value
            )
          }
        />
      </section>

      <section className={sectionClass}>
        <div className="mb-5">
          <h3 className={sectionTitleClass}>
            Geographic Preferences
          </h3>
          <p className={sectionHelpClass}>
            Choose native origins first, then countries and specific locations.
          </p>
        </div>

        <div className="space-y-6">
          <MultiSelectField
            label="Preferred Native Places"
            options={nativeOptions}
            value={formData.preferredNativeOrigins || []}
            searchValue={nativeInput}
            loading={nativeLoading}
            onSearchChange={searchNativePlaces}
            onChange={(value) =>
              setFieldValue("preferredNativeOrigins", value)
            }
            placeholder="Type to search native place"
          />

          <MultiSelectField
            label="Preferred Countries"
            options={preferredCountryOptions}
            value={formData.preferredCountries || []}
            onChange={(value) =>
              setFieldValue("preferredCountries", value)
            }
            placeholder="Select preferred countries"
          />

          <MultiCountryStateCitySelector
            label="Preferred Locations"
            value={formData.preferredCities || []}
            onChange={(value) =>
              setFieldValue("preferredCities", value)
            }
          />
        </div>
      </section>

      <section className={sectionClass}>
        <div className="mb-5">
          <h3 className={sectionTitleClass}>
            Partner Expectations
          </h3>
          <p className={sectionHelpClass}>
            Share any additional expectations that are important to you.
          </p>
        </div>

        <label className="block text-sm font-medium text-slate-700">
          Expectations
          <textarea
            name="expectations"
            value={formData.expectations || ""}
            onChange={updateField}
            maxLength={1000}
            rows={5}
            className={inputClass}
          />
          <div className="mt-1.5 flex justify-end text-xs">
            <span className="text-slate-500">
              {expectationsCount}/1000
            </span>
          </div>
        </label>
      </section>
    </div>
  );
};

export default PartnerPreferencesSection;
